import React, { useCallback, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getThumbUrl } from '../../shared/api';
import { useAuth } from '../../shared/AuthContext';
import { COLOR_DANGER, COLOR_INFO, getContrastColor } from '../../shared/colorUtils';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import AssistantChat from '../assistant/AssistantChat';
import ChatRoom from './ChatRoom';
import NewRoomModal from './NewRoomModal';
import './Chat.css';

const ASSISTANT_ROOM_ID = 'assistant';

const Chat = () => {
  const { t } = useTranslation();
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const { user } = useAuth();

  const [showNewRoom, setShowNewRoom] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const queryClient = useQueryClient();
  const { data: rooms = [], isLoading: loading } = useQuery({
    queryKey: ['chat', 'rooms'],
    queryFn: async () => {
      const data = await api.get('chat/rooms/');
      return data.results ?? data;
    },
  });

  const fetchRooms = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['chat', 'rooms'] });
  }, [queryClient]);

  const activeRoomId = roomId || ASSISTANT_ROOM_ID;

  // Refresh sidebar when a room is created or updated via the live channel
  useRealtimeEvents((e) => {
    if (e.model !== 'chat.room') return;
    fetchRooms();
  });

  const handleRoomCreated = (room) => {
    setShowNewRoom(false);
    fetchRooms();
    navigate(`/chat/${room.id}`);
  };

  const totalUnread = rooms.reduce((sum, r) => sum + (r.unread_count || 0), 0);

  return (
    <div className="chat-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="chat-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`chat-sidebar${sidebarOpen ? ' chat-sidebar--open' : ''}`}>
        <div className="chat-sidebar-header">
          <span className="chat-sidebar-title">{t('chat.title')}</span>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setShowNewRoom(true)}
            title={t('chat.newRoom')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 5l0 14" />
              <path d="M5 12l14 0" />
            </svg>
          </button>
        </div>

        {/* AI Assistant pinned entry */}
        <div
          className={`chat-room-item chat-room-item--ai${activeRoomId === ASSISTANT_ROOM_ID ? ' chat-room-item--active' : ''}`}
          onClick={() => { navigate('/chat'); setSidebarOpen(false); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/chat')}
        >
          <div className="chat-room-avatar chat-room-avatar--ai">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M15.5 13a3.5 3.5 0 0 0 -3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8" />
              <path d="M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1 -7 0v-1.8" />
              <path d="M17.5 16a3.5 3.5 0 0 0 0 -7h-.5" />
              <path d="M19 9.3v-2.8a3.5 3.5 0 0 0 -7 0" />
              <path d="M6.5 16a3.5 3.5 0 0 1 0 -7h.5" />
              <path d="M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10" />
            </svg>
          </div>
          <div className="chat-room-info d-flex flex-column">
            <span className="chat-room-name">{t('chat.aiAssistant')}</span>
            <span className="chat-room-preview">{t('chat.aiAssistantDesc')}</span>
          </div>
        </div>

        {/* Divider */}
        {rooms.length > 0 && <div className="chat-sidebar-divider" />}

        {/* User rooms */}
        {loading ? (
          <div className="chat-sidebar-loading">
            <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : (
          rooms.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              active={String(room.id) === String(roomId)}
              currentUserId={user?.id}
              onClick={() => { navigate(`/chat/${room.id}`); setSidebarOpen(false); }}
            />
          ))
        )}
      </aside>

      {/* Main panel */}
      <div className="chat-main">
        {/* Mobile header bar */}
        <div className="chat-mobile-bar d-md-none">
          <button className="btn btn-icon btn-ghost-secondary" onClick={() => setSidebarOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 6l16 0" /><path d="M4 12l16 0" /><path d="M4 18l16 0" />
            </svg>
            {totalUnread > 0 && (
              <span
                className="badge badge-notification"
                style={{ backgroundColor: COLOR_DANGER, color: getContrastColor(COLOR_DANGER) }}
              >{totalUnread}</span>
            )}
          </button>
          <span className="chat-mobile-title">{t('chat.title')}</span>
        </div>

        {activeRoomId === ASSISTANT_ROOM_ID ? (
          <AssistantChat embedded initialInput={initialQuery} />
        ) : (
          <ChatRoom
            key={activeRoomId}
            roomId={activeRoomId}
            roomInfo={rooms.find((r) => String(r.id) === String(activeRoomId))}
            onRoomsRefresh={fetchRooms}
          />
        )}
      </div>

      {showNewRoom && (
        <NewRoomModal
          onClose={() => setShowNewRoom(false)}
          onCreated={handleRoomCreated}
        />
      )}
    </div>
  );
};

// ── Sidebar room item ──────────────────────────────────────────────────────

function RoomItem({ room, active, currentUserId, onClick }) {
  const { t } = useTranslation();
  const isGroup = room.type === 'GROUP';
  const initials = (room.display_name || '')
    .split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');

  // For direct rooms, find the other participant to get their avatar.
  const otherMember = !isGroup
    ? (room.members || []).find((m) => m.user?.id !== currentUserId)?.user
    : null;
  const avatarUrl = otherMember?.avatar || null;

  const lastMsg = room.last_message;
  let preview = lastMsg?.content || '';
  if (preview.length > 45) preview = preview.slice(0, 42) + '…';

  const time = lastMsg ? formatTime(lastMsg.created_at) : '';

  return (
    <div
      className={`chat-room-item${active ? ' chat-room-item--active' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className={`chat-room-avatar${isGroup ? ' chat-room-avatar--group' : ''}`}>
        {isGroup ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
            <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
            <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
            <path d="M17 10h2a2 2 0 0 1 2 2v1" />
            <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
            <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
          </svg>
        ) : avatarUrl ? (
          <img src={getThumbUrl(avatarUrl, 200)} alt={room.display_name} />
        ) : (
          <span>{initials || '?'}</span>
        )}
      </div>
      <div className="chat-room-info">
        <div className="chat-room-name-row">
          <span className="chat-room-name">{room.display_name}</span>
          {time && <span className="chat-room-time">{time}</span>}
        </div>
        <div className="chat-room-preview-row">
          <span className="chat-room-preview">{preview || t('chat.noMessages')}</span>
          {room.unread_count > 0 && (
            <span
              className="badge chat-unread-badge"
              style={{ backgroundColor: COLOR_INFO, color: getContrastColor(COLOR_INFO) }}
            >{room.unread_count}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}

export default Chat;
